import { useTranslation } from "react-i18next";
import { HealthBar } from "./HealthBar";
import type { BattleOpponentVisual } from "./BattleActors";
import { getEnemySpritePath, getPlayerSpritePath } from "../assets/characterSprites";

const TIMER_ICON = (
  <svg viewBox="0 0 48 48">
    <path className="timer__frame" d="M13 5h22v5H13zm0 33h22v5H13z" />
    <path className="timer__glass" d="M17 10h14c0 7-2.8 9.7-7 13 4.2 3.3 7 6 7 15H17c0-9 2.8-11.7 7-15-4.2-3.3-7-6-7-13Z" />
    <path className="timer__sand" d="m20 13 4 6 4-6zm4 14c-2.4 2-3.6 4.2-3.8 8h7.6c-.2-3.8-1.4-6-3.8-8Z" />
  </svg>
);

interface BattleHudProps {
  playerHp: number;
  botHp: number;
  playerMaxHp: number;
  botMaxHp: number;
  secondsLeft: number;
  playerName?: string;
  botName?: string;
  opponent?: BattleOpponentVisual;
}

export function BattleHud({
  playerHp,
  botHp,
  playerMaxHp,
  botMaxHp,
  secondsLeft,
  playerName,
  botName,
  opponent
}: BattleHudProps) {
  const { t } = useTranslation();
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const resolvedPlayerName = playerName ?? t("playerFallbackName");
  const resolvedBotName = botName ?? t("opponentName");

  return (
    <header className="battle-hud">
      <div className="portrait portrait--player" aria-hidden="true">
        <img
          className="portrait__sprite portrait__sprite--player"
          src={getPlayerSpritePath("idle")}
          alt=""
          draggable={false}
        />
      </div>
      <HealthBar hp={playerHp} maxHp={playerMaxHp} label={resolvedPlayerName} side="player" />
      <div className="timer" aria-label={t("timer", { seconds: secondsLeft })}>
        <span className="timer__icon" aria-hidden="true">
          {TIMER_ICON}
        </span>
        <time className="timer__value" dateTime={`PT${secondsLeft}S`}>
          {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </time>
      </div>
      <HealthBar hp={botHp} maxHp={botMaxHp} label={resolvedBotName} side="bot" />
      <div className="portrait portrait--bot" aria-hidden="true">
        <img
          className="portrait__sprite portrait__sprite--bot"
          src={getEnemySpritePath(opponent?.variant ?? 3, "idle")}
          alt=""
          draggable={false}
        />
      </div>
    </header>
  );
}
